import styled from '@emotion/styled';
import { buttons } from '../theme/tokens';
import { ReactNode } from 'react';

type ButtonVariant = 'filled' | 'outline';
type ButtonColor = 'primary' | 'warning' | 'danger';

interface ButtonProps {
  variant?: ButtonVariant;
  color?: ButtonColor;
  icon?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}

interface StyledButtonProps {
  buttonVariant: ButtonVariant;
  buttonColor: ButtonColor;
  hasIcon: boolean;
}

const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.hasIcon ? '0.5rem' : '0'};
  padding: ${buttons.sizes.default.padding};
  font-size: ${buttons.sizes.default.fontSize};
  font-family: 'Gabarito', sans-serif;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  text-decoration: none;
  
  ${props => {
    const style = buttons.variants[props.buttonVariant]?.[props.buttonColor];
    if (!style) {
      // Fallback to primary filled if the requested variant/color doesn't exist
      const fallbackStyle = buttons.variants.filled.primary;
      const isOutline = props.buttonVariant === 'outline';
      return `
        background: ${fallbackStyle.background};
        color: ${fallbackStyle.color};
        ${isOutline ? `border: 1px solid ${fallbackStyle.background};` : ''}
        
        &:hover:not(:disabled) {
          background: ${fallbackStyle.hoverBackground};
          color: ${fallbackStyle.hoverColor};
        }
      `;
    }
    const isOutline = props.buttonVariant === 'outline';
    return `
      background: ${style.background};
      color: ${style.color};
      ${isOutline ? `border: ${(style as any).border};` : ''}
      
      &:hover:not(:disabled) {
        background: ${style.hoverBackground};
        color: ${style.hoverColor};
      }
    `;
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: ${buttons.sizes.default.iconSize};
    height: ${buttons.sizes.default.iconSize};
  }
`;

export const Button = ({
  variant = 'filled',
  color = 'primary',
  icon,
  children,
  onClick,
  type = 'button',
  disabled,
  ...props
}: ButtonProps) => {
  return (
    <StyledButton
      buttonVariant={variant}
      buttonColor={color}
      hasIcon={!!icon}
      onClick={onClick}
      type={type}
      disabled={disabled}
      {...props}
    >
      {icon}
      {children}
    </StyledButton>
  );
};

export default Button; 